import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { ChevronDown, Check } from 'lucide-react'
import { AnchoredMenu } from 'components/ui/AnchoredMenu'
import { cn } from 'lib/utils'

interface SelectOption {
  value: string
  label: string
}

interface CustomSelectProps {
  options: SelectOption[]
  value?: string
  onChange?: (value: string) => void
  onBlur?: () => void
  placeholder?: string
  error?: string
  label?: string
  required?: boolean
  description?: string
  disabled?: boolean
  className?: string
}

type DropdownPosition = {
  top: number
  left: number
  width: number
  maxHeight: number
  openUpward: boolean
}

export function CustomSelect({
  options,
  value,
  onChange,
  onBlur,
  placeholder = "Sélectionner une option",
  error,
  label,
  required = false,
  description,
  disabled = false,
  className
}: CustomSelectProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedValue, setSelectedValue] = useState(value || '')
  const [dropdownPosition, setDropdownPosition] = useState<DropdownPosition | null>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  const selectedOption = options.find(option => option.value === selectedValue)

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return

    const rect = triggerRef.current.getBoundingClientRect()
    const viewportHeight = window.innerHeight
    const spaceBelow = viewportHeight - rect.bottom - 8
    const spaceAbove = rect.top - 8
    const preferredMaxHeight = 240
    const openUpward = spaceBelow < Math.min(preferredMaxHeight, options.length * 40) && spaceAbove > spaceBelow
    const maxHeight = Math.max(120, Math.min(preferredMaxHeight, openUpward ? spaceAbove : spaceBelow))

    setDropdownPosition({
      top: openUpward ? rect.top - 4 : rect.bottom + 4,
      left: rect.left,
      width: rect.width,
      maxHeight,
      openUpward,
    })
  }, [options.length])

  useEffect(() => {
    if (value !== undefined) {
      setSelectedValue(value)
    }
  }, [value])

  useLayoutEffect(() => {
    if (!isOpen) {
      setDropdownPosition(null)
      return
    }

    updatePosition()

    const handleReposition = () => updatePosition()
    window.addEventListener('resize', handleReposition)
    // Capture scroll from nested modal containers as well.
    window.addEventListener('scroll', handleReposition, true)

    return () => {
      window.removeEventListener('resize', handleReposition)
      window.removeEventListener('scroll', handleReposition, true)
    }
  }, [isOpen, updatePosition])

  useEffect(() => {
    if (!isOpen) return

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      const clickedTrigger = triggerRef.current?.contains(target)
      const clickedList = listRef.current?.contains(target)
      if (!clickedTrigger && !clickedList) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  const handleSelect = (optionValue: string) => {
    setSelectedValue(optionValue)
    onChange?.(optionValue)
    onBlur?.()
    setIsOpen(false)
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (disabled) return

    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault()
        setIsOpen(!isOpen)
        break
      case 'Escape':
        setIsOpen(false)
        break
      case 'ArrowDown':
      case 'ArrowUp':
        event.preventDefault()
        if (!isOpen) {
          setIsOpen(true)
        }
        break
    }
  }

  const dropdownMenu = isOpen && dropdownPosition
    ? createPortal(
        <div
          ref={listRef}
          role="listbox"
          className="fixed z-[200] bg-background border border-input rounded-md shadow-lg overflow-auto"
          style={{
            top: dropdownPosition.openUpward ? undefined : dropdownPosition.top,
            bottom: dropdownPosition.openUpward
              ? window.innerHeight - dropdownPosition.top
              : undefined,
            left: dropdownPosition.left,
            width: dropdownPosition.width,
            maxHeight: dropdownPosition.maxHeight,
          }}
        >
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              role="option"
              aria-selected={selectedValue === option.value}
              className={cn(
                "flex w-full items-center justify-between px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-none",
                selectedValue === option.value && "bg-accent text-accent-foreground"
              )}
              onClick={() => handleSelect(option.value)}
            >
              <span className="whitespace-nowrap overflow-hidden text-ellipsis block min-w-0 flex-1 text-left">
                {option.label}
              </span>
              {selectedValue === option.value && (
                <Check className="h-4 w-4 shrink-0" />
              )}
            </button>
          ))}
        </div>,
        document.body
      )
    : null

  return (
    <div className="space-y-2">
      {label && (
        <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </label>
      )}
      {description && (
        <p className="text-sm text-muted-foreground">{description}</p>
      )}
      <div className="relative">
        <button
          ref={triggerRef}
          type="button"
          className={cn(
            "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
            error && "border-destructive focus:ring-destructive",
            isOpen && "ring-2 ring-ring",
            className
          )}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
        >
          <span className={cn(
            selectedOption ? "text-foreground" : "text-muted-foreground",
            "whitespace-nowrap overflow-hidden text-ellipsis block min-w-0 flex-1 text-left"
          )}>
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          <ChevronDown className={cn(
            "h-4 w-4 text-muted-foreground transition-transform shrink-0",
            isOpen && "rotate-180"
          )} />
        </button>

        {dropdownMenu}
      </div>
      {error && (
        <p className="text-sm text-destructive">{error}</p>
      )}
    </div>
  )
}
